// Dashboard del módulo Control Bancario (Tesorería). Control Bancario → Resumen.
// RPC cb_dashboard (script 201). Paleta categórica validada (dataviz):
// teal #0E9488 / violeta #7C5CDB / coral #E05A3A sobre superficie clara.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  AlertTriangle, Banknote, CalendarClock, ClipboardCheck, Landmark, ListChecks, Loader2,
  PiggyBank, PlugZap, TrendingDown, TrendingUp, Wallet,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { cbRpc, money } from './shared';
import { useThemeStore } from '@/stores/theme.store';

const TEAL = '#0E9488';
const STARK_TEAL = '#22d3ee';

interface DashData {
  posicion: { total: number; n_cuentas: number; por_moneda: { moneda: string; n: number; saldo: number }[]; por_banco: { banco: string; saldo: number }[] };
  cheques: { girados_n: number; girados_monto: number; posfechados_n: number };
  conciliacion: { mov_pendientes: number; lin_pendientes: number };
  caja_chica: { n_cajas: number; total_efectivo: number; total_pendiente: number };
  tesoreria: { programados_n: number; programados_monto: number; op_borrador: number; op_aprobadas: number };
  flujo: { saldo_actual: number; egresos_30: number; saldo_proyectado_30: number };
  mov_mes: { ingresos: number; egresos: number };
  proximos: { fecha: string; tipo: string; detalle: string | null; monto: number }[];
}

function Kpi({ icon, label, valor, detalle, clase }: { icon: React.ReactNode; label: string; valor: string; detalle?: string; clase?: string }) {
  return (
    <Card><CardContent className="py-3 flex items-start gap-3">
      <div className="text-primary mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={`text-lg font-semibold font-mono tabular-nums truncate ${clase ?? ''}`}>{valor}</div>
        {detalle && <div className="text-xs text-muted-foreground">{detalle}</div>}
      </div>
    </CardContent></Card>
  );
}

function Alerta({ n, texto, ok }: { n: number; texto: string; ok?: string }) {
  const activa = n > 0;
  return (
    <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md ${activa ? 'bg-pendiente-soft text-pendiente' : 'bg-estable-soft text-estable'}`}>
      <AlertTriangle className={`w-4 h-4 ${activa ? '' : 'opacity-40'}`} />
      <span>{activa ? texto : (ok ?? texto)}</span>
      <span className="ml-auto font-mono font-semibold">{n}</span>
    </div>
  );
}

interface TesEstado {
  cuentas: number; bancos: number; movimientos: number;
  detenido: { efectivo_en_transito: number; facturas_por_pagar: number; importe_por_pagar: number;
              roles_sin_pagar: number; sesiones_sin_depositar: number };
  excepciones: number; cuentas_sin_contable: number;
}

// Sin una sola cuenta de tesorería, el ciclo se acaba en la factura: no se
// puede depositar el efectivo del POS, ni pagar a un proveedor, ni marcar
// pagada una nómina. Eso no se parece a una elección, así que conviene que el
// panel diga lo que está esperando — y cuánto.
function ArranqueTesoreria() {
  const qc = useQueryClient();
  const { data } = useQuery<TesEstado>({
    queryKey: ['cb-tesoreria-estado'], queryFn: () => cbRpc<TesEstado>('cb_tesoreria_estado'),
  });
  const iniciar = useMutation({
    mutationFn: () => cbRpc<any>('cb_tesoreria_iniciar', { payload: { tipo: 'efectivo', alias: 'Caja' } }),
    onSuccess: (r: any) => {
      toast.success(r?.mensaje || 'Cuenta creada');
      void qc.invalidateQueries({ queryKey: ['cb-tesoreria-estado'] });
      void qc.invalidateQueries({ queryKey: ['cb-dashboard'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!data || data.cuentas > 0) {
    // Con cuentas creadas sólo queda un aviso, y sólo si hace falta.
    if (data && (data.cuentas_sin_contable > 0 || data.excepciones > 0)) {
      return (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-800 dark:bg-amber-950/30">
          {data.cuentas_sin_contable > 0 && (
            <p className="text-amber-900 dark:text-amber-200">
              <b>{data.cuentas_sin_contable}</b> cuenta(s) de tesorería sin cuenta contable asignada:
              sus movimientos no generan asiento y sólo aparecen en la bandeja de excepciones.
            </p>)}
          {data.excepciones > 0 && (
            <p className="mt-1 text-amber-900 dark:text-amber-200">
              <b>{data.excepciones}</b> movimiento(s) bancarios no se pudieron contabilizar. Están en
              Contabilidad → Excepciones con el motivo.
            </p>)}
        </div>
      );
    }
    return null;
  }

  const d = data.detenido;
  return (
    <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-5">
      <div className="flex items-start gap-3">
        <PlugZap className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">La tesorería todavía no está arrancada</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            No hay ninguna cuenta creada, así que el ciclo se acaba en la factura: se registra lo que la
            empresa debe y nada de lo que paga. Esto es lo que está esperando:
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-md border bg-card p-2 text-sm">
              <span className="block text-[11px] text-muted-foreground">Efectivo del POS sin depositar</span>
              <span className="font-mono tabular-nums">{money(d.efectivo_en_transito)}</span></div>
            <div className="rounded-md border bg-card p-2 text-sm">
              <span className="block text-[11px] text-muted-foreground">Facturas por pagar</span>
              <span className="font-mono tabular-nums">{d.facturas_por_pagar} · {money(d.importe_por_pagar)}</span></div>
            <div className="rounded-md border bg-card p-2 text-sm">
              <span className="block text-[11px] text-muted-foreground">Roles de nómina sin pagar</span>
              <span className="font-mono tabular-nums">{d.roles_sin_pagar}</span></div>
            <div className="rounded-md border bg-card p-2 text-sm">
              <span className="block text-[11px] text-muted-foreground">Cierres de caja sin depositar</span>
              <span className="font-mono tabular-nums">{d.sesiones_sin_depositar}</span></div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              disabled={iniciar.isPending}
              onClick={() => iniciar.mutate()}
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50">
              Crear la caja de efectivo
            </button>
            <p className="text-xs text-muted-foreground">
              Crea una cuenta de tipo <b>efectivo</b> mapeada a la cuenta contable de Caja — sin datos
              bancarios. Para una cuenta de banco, ve a <b>Catálogos → Cuentas Bancarias</b>: el catálogo
              de bancos del Ecuador ya está cargado.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DashboardControlBancario() {
  const isStark = useThemeStore((st) => st.currentThemeId === 'stark-hud');
  const cBar = isStark ? STARK_TEAL : TEAL;
  const { data, isFetching } = useQuery<DashData>({
    queryKey: ['cb-dashboard'],
    queryFn: () => cbRpc<DashData>('cb_dashboard'),
    staleTime: 0,
  });

  const monedaPrincipal = data?.posicion.por_moneda[0]?.moneda ?? 'USD';
  const flujoNeg = data && data.flujo.saldo_proyectado_30 < 0;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold">Dashboard · Control Bancario</h1>
          <p className="text-sm text-muted-foreground">Tesorería: posición, cheques, conciliación, caja chica y flujo</p>
        </div>
        {isFetching && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
      </div>

      <ArranqueTesoreria />

      {/* KPIs principales */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi icon={<Wallet className="w-5 h-5" />} label="Saldo total en bancos" valor={money(data?.posicion.total, monedaPrincipal)} detalle={`${data?.posicion.n_cuentas ?? 0} cuenta(s)`} />
        <Kpi icon={<Banknote className="w-5 h-5" />} label="Cheques en circulación" valor={money(data?.cheques.girados_monto, monedaPrincipal)} detalle={`${data?.cheques.girados_n ?? 0} girado(s) · ${data?.cheques.posfechados_n ?? 0} posf.`} />
        <Kpi icon={<PiggyBank className="w-5 h-5" />} label="Caja chica (efectivo)" valor={money(data?.caja_chica.total_efectivo, monedaPrincipal)} detalle={`${data?.caja_chica.n_cajas ?? 0} caja(s)`} />
        <Kpi icon={<TrendingUp className="w-5 h-5" />} label="Ingresos del mes" valor={money(data?.mov_mes.ingresos, monedaPrincipal)} clase="text-estable" />
        <Kpi icon={<TrendingDown className="w-5 h-5" />} label="Egresos del mes" valor={money(data?.mov_mes.egresos, monedaPrincipal)} clase="text-pendiente" />
        <Kpi icon={<CalendarClock className="w-5 h-5" />} label="Saldo proyectado (30d)" valor={money(data?.flujo.saldo_proyectado_30, monedaPrincipal)} detalle={`− ${money(data?.flujo.egresos_30, monedaPrincipal)} comprom.`} clase={flujoNeg ? 'text-pendiente' : ''} />
      </div>

      {/* Alertas operativas */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <Alerta n={data?.conciliacion.mov_pendientes ?? 0} texto="Movimientos por conciliar" ok="Todo conciliado" />
        <Alerta n={data?.conciliacion.lin_pendientes ?? 0} texto="Líneas de extracto pendientes" ok="Extractos al día" />
        <Alerta n={data?.tesoreria.op_borrador ?? 0} texto="Órdenes de pago por aprobar" ok="Sin OP en borrador" />
        <Alerta n={data?.tesoreria.op_aprobadas ?? 0} texto="Órdenes aprobadas por pagar" ok="Sin OP por pagar" />
      </div>

      {flujoNeg && (
        <div className="flex items-center gap-2 rounded-md bg-pendiente-soft text-pendiente px-3 py-2 text-sm">
          <AlertTriangle className="w-4 h-4" /> El saldo proyectado a 30 días es negativo ({money(data?.flujo.saldo_proyectado_30, monedaPrincipal)}): revisa la programación de pagos.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Posición por banco */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Landmark className="w-4 h-4" /> Posición por banco</CardTitle></CardHeader>
          <CardContent>
            {(data?.posicion.por_banco.length ?? 0) === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Sin cuentas registradas</p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(160, (data!.posicion.por_banco.length) * 40)}>
                <BarChart data={data!.posicion.por_banco} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} className="stroke-muted" />
                  <XAxis type="number" tickFormatter={(v) => money(v, monedaPrincipal)} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="banco" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => money(v, monedaPrincipal)} />
                  <Bar dataKey="saldo" fill={cBar} radius={[0, 4, 4, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            )}
            {(data?.posicion.por_moneda.length ?? 0) > 1 && (
              <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                {data!.posicion.por_moneda.map((m) => <span key={m.moneda}>{m.moneda}: <span className="font-mono">{money(m.saldo, m.moneda)}</span></span>)}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Próximos vencimientos */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Próximos vencimientos</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Detalle</th><th className="py-2 pr-3 text-right">Monto</th>
              </tr></thead>
              <tbody>
                {(data?.proximos.length ?? 0) === 0 && <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">Sin compromisos próximos</td></tr>}
                {(data?.proximos ?? []).map((p, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 pr-3 whitespace-nowrap">{p.fecha}</td>
                    <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{p.tipo}</span></td>
                    <td className="py-2 pr-3">{p.detalle ?? '—'}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums text-pendiente">{money(p.monto, monedaPrincipal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Resumen inferior */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={<ListChecks className="w-5 h-5" />} label="Vales por reponer (caja chica)" valor={money(data?.caja_chica.total_pendiente, monedaPrincipal)} />
        <Kpi icon={<CalendarClock className="w-5 h-5" />} label="Pagos programados" valor={money(data?.tesoreria.programados_monto, monedaPrincipal)} detalle={`${data?.tesoreria.programados_n ?? 0} pendiente(s)`} />
        <Kpi icon={<ClipboardCheck className="w-5 h-5" />} label="Por conciliar (libro+banco)" valor={String((data?.conciliacion.mov_pendientes ?? 0) + (data?.conciliacion.lin_pendientes ?? 0))} detalle="partidas pendientes" />
        <Kpi icon={<Banknote className="w-5 h-5" />} label="Cheques posfechados" valor={String(data?.cheques.posfechados_n ?? 0)} detalle="por vencer" />
      </div>
    </div>
  );
}
